import { ReactNode } from "react";
import { isEmpty } from "lodash";
import { InvalidQueryConfig } from "../../../generated/graphql";
import { spacing } from "../../../stitches.config";
import { BANNER_TYPES } from "../__styles__/Banner";
import { FlexRow } from "../__styles__/Layout";
import { Emphasis } from "../__styles__/Typography";
import Banner from "../Banner";
import { stripCustomMapFieldPrefix } from "./utils";

export interface InvalidQueryConfigBannerProps {
  invalidQueryConfig?: InvalidQueryConfig | null;
  isSavedView: boolean;
}

export const InvalidQueryConfigBanner = ({
  invalidQueryConfig,
  isSavedView,
}: InvalidQueryConfigBannerProps) => {
  if (!invalidQueryConfig) {
    return null;
  }

  const { fields, filters, orderByReset, columnHeaderIssues } =
    invalidQueryConfig;
  const hasInvalidFields = !isEmpty(fields);
  const hasInvalidFilters = !isEmpty(filters);
  const hasColumnHeaderIssues = !isEmpty(columnHeaderIssues);
  const hasInvalidData = hasInvalidFields || hasInvalidFilters || orderByReset;

  if (!hasInvalidData && !hasColumnHeaderIssues) {
    return null;
  }
  const savedViewOrQuery = isSavedView ? "saved view" : "query";

  const headingParts: ReactNode[] = [];

  if (hasInvalidData) {
    headingParts.push(
      <span key="invalid-data">
        <Emphasis>
          This {savedViewOrQuery} references data that no longer exists.
        </Emphasis>{" "}
        Affected columns and filters are highlighted in red. Remove them and
        re-save to dismiss this warning.
      </span>,
    );
  }

  if (hasColumnHeaderIssues) {
    headingParts.push(
      <span key="column-header-issues">
        <Emphasis>
          This {savedViewOrQuery} has columns that can't be exported.
        </Emphasis>{" "}
        Affected columns are highlighted in yellow. Contact your Forerunner
        admin for help to dismiss this warning.
      </span>,
    );
  }

  const listItems: ReactNode[] = [];

  if (hasInvalidFields) {
    const fieldNames = fields.map(f => stripCustomMapFieldPrefix(f.name));
    listItems.push(
      ...fieldNames.map(name => (
        <li key={`field-${name}`}>
          <Emphasis>{name}</Emphasis> — This column references a field that has
          been removed
        </li>
      )),
    );
  }

  if (hasInvalidFilters) {
    const filterNames = filters.map(f => stripCustomMapFieldPrefix(f.field));
    listItems.push(
      ...filterNames.map(name => (
        <li key={`filter-${name}`}>
          <Emphasis>{name}</Emphasis> — This filter references a field that has
          been removed
        </li>
      )),
    );
  }

  if (orderByReset) {
    listItems.push(<li key="orderBy">Sort order was reset to default</li>);
  }

  if (hasColumnHeaderIssues) {
    const tooLong = columnHeaderIssues.filter(i => i.issue === "TOO_LONG");
    const duplicates = columnHeaderIssues.filter(i => i.issue === "DUPLICATE");

    if (tooLong.length > 0) {
      listItems.push(
        ...tooLong.map(i => (
          <li key={`tooLong-${i.name}`}>
            <Emphasis>{i.header}</Emphasis> — Column header exceeds the export
            character limit
          </li>
        )),
      );
    }

    if (duplicates.length > 0) {
      listItems.push(
        ...duplicates.map(i => (
          <li key={`duplicate-${i.name}`}>
            <Emphasis>{i.header}</Emphasis> — Column header is a duplicate —
            exports require unique headers
          </li>
        )),
      );
    }
  }

  return (
    <FlexRow
      style={{
        padding: `0px ${spacing.l} ${spacing.m} ${spacing.l}`,
      }}
    >
      <Banner
        items={[
          <div key="invalid-query-config-message">
            {headingParts.map((part, i) => (
              <div key={i}>{part}</div>
            ))}
            <ul
              style={{
                margin: "4px 0 0 0",
                paddingLeft: "24px",
              }}
            >
              {listItems}
            </ul>
          </div>,
        ]}
        type={BANNER_TYPES.ATTENTION}
      />
    </FlexRow>
  );
};
